/* eslint-disable global-require */
import { I_CommentScene, I_Hotspot, I_Scene } from '@containers/Home/types';
import { debounce, generateRandomID, isServer } from '@utils/helper';
import { renderToString } from 'react-dom/server';
import ReactDOM from 'react-dom';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useState,
  memo,
  useImperativeHandle,
  useRef,
} from 'react';
import { useSelector } from 'react-redux';
import dynamic from 'next/dynamic';
import {
  selectHotspotConfigGlobal,
  selectIsFirstScene,
  selectListPostUsedOnTour,
  selectPolygonConfigGlobal,
} from '@containers/Home/selectors';
import Image from '@assets/img/Rectangle.png';
import {
  useCollaborationMode,
  useTourEventState,
} from '@containers/Home/global/TourStats';
import { useCommentScene } from '@containers/Home/hook/useCommentScene';
import CommentSceneIcon, {
  TypeRefCommentSceneIcon,
} from '@containers/Home/components/Collaboration/CommentScene/CommentSceneIcon';
import HotspotMedia, {
  TypeRefHotspotMedia,
} from '@components/Hotspot/HotspotMedia';
import KRPANO from './KrpanoTemplate';
import { IPropKrpano, Type_RefFuncKrpano } from './types';
import { useInternalFunction } from './useInternalFunction';

const Hotspot = dynamic(() => import('@components/Hotspot'));
const _window = isServer ? undefined : (window as any);

const KrpanoViewer = forwardRef(
  (
    {
      scene,
      listScene,
      urlTour,
      divId = 'container',
      krpanoId = 'krpanoContainer',
      onClickHotspot,
      onChangeAutoChangeScene,
      isLoaded,
      setIsLoaded,
      nadirConfig,
      children,
      styleContainer = { height: '100%' },
      isTurnOffFollowMode = false,
    }: IPropKrpano,
    ref: React.Ref<Type_RefFuncKrpano>,
  ) => {
    const isLoadedRef = useRef(false);
    const isAllowTriggerSoundRef = useRef(false);
    const containerRef = useRef<HTMLDivElement>(null!);
    // NOTE: store to control close/unmount popover when change scene/commentScene
    const listCommentSceneIconRef = useRef<
      Map<string, TypeRefCommentSceneIcon>
    >(new Map([]));
    // NOTE: store to control volume (mute/unmute) on hotspot media video
    const listHotspotMediaVideoRef = useRef<Map<string, TypeRefHotspotMedia>>(
      new Map([]),
    );

    const isFirstScene = useSelector(selectIsFirstScene);
    const listPost = useSelector(selectListPostUsedOnTour);
    const hotspotConfigGlobal = useSelector(selectHotspotConfigGlobal);
    const polygonHotspotConfigGlobal = useSelector(selectPolygonConfigGlobal);

    const [krpanoView, setKrpanoView] = useState<any>();
    const [isAllowTriggerSound, setIsAllowTriggerSound] = useState(false);

    const [tourEventStats] = useTourEventState('state');

    const { onCreateCommentScene, onUpdateCommentScene, onDeleteCommentScene } =
      useCommentScene();
    const [, setIsShowDialogCreateCommentScene] = useCollaborationMode(
      'isShowDialogCreateCommentScene',
    );
    const [, setCommentSceneTemporary] = useCollaborationMode(
      'commentSceneTemporary',
    );
    const [, setCommentSelected] = useCollaborationMode('commentSceneSelected');

    const {
      sceneChilds,
      onRenderXmlKrpano,
      onTriggerChangeView,
      onTriggerChangeZoom,
      onTriggerSoundHotspot,
      onTriggerLoadCompleted,
      onAddHotspotNadirKrpano,
      onAddHotspotKrpanoUntils,
      onAddCommentSceneIconUntils,
      onApplyConfigLittlePlanetKrpano,
      onApplyConfigPluginKrpanoForScene,
    } = useInternalFunction({
      scene,
      krpanoId,
    });

    // NOTE: use ref to store state because function used in event onclick hotspot (state won't update on event's function)
    useEffect(() => {
      isAllowTriggerSoundRef.current = isAllowTriggerSound;
    }, [isAllowTriggerSound]);

    // NOTE: reset state when scene change => only trigger sound when isAllowTriggerSound = true
    useEffect(
      () => () => {
        setIsAllowTriggerSound(false);
        listCommentSceneIconRef.current = new Map([]);
        listHotspotMediaVideoRef.current = new Map([]);
      },
      [scene?.id],
    );

    /**
     * SECTION: LOAD_SCENE_TO_KRPANO
     * @param {scene}: current scene selected
     * @param {sceneXml}: default by null, use krpano_xml on media if exist.
     */

    const loadSceneToView = useCallback(
      (myscene: I_Scene) => {
        if (myscene && myscene.media && krpanoView) {
          const { transition, view, fovDefault } = onRenderXmlKrpano();
          krpanoView.loadScene(
            urlTour,
            null,
            myscene.media.krpano_xml || null,
            transition,
            view,
            myscene.mode || '',
            sceneChilds,
            myscene.configStaging || {},
            fovDefault,
          );
        }
      },
      [krpanoView, onRenderXmlKrpano, urlTour, sceneChilds],
    );

    /**
     * !SECTION: END_LOAD_SCENE_TO_KRPANO
     */

    const onAddHotspotKrpano = useCallback(
      (hotspot: I_Hotspot, isReloadHotspot = false) => {
        const {
          name,
          krpano,
          onConfigHotspotMedia,
          onConfigHotspotPolygon,
          onConfigHotspotInstructor,
        } = onAddHotspotKrpanoUntils(hotspot);

        async function renderHotspot() {
          const hs = krpano.get(`hotspot[${name}]`);
          const contentString = <div id={name} className="inline-flex" />;
          hs.sprite.innerHTML = renderToString(contentString).toString();
          const node = document.getElementById(name);
          if (node) {
            ReactDOM.render(
              hotspot.type === 'media' ? (
                <HotspotMedia
                  ref={r => {
                    if (
                      r &&
                      hotspot.config?.media?.type === 'media-video' &&
                      !listHotspotMediaVideoRef.current.get(hotspot.id)
                    )
                      listHotspotMediaVideoRef.current.set(hotspot.id, r);
                  }}
                  onClickHotspot={onClickHotspot}
                  isSoundOn={isAllowTriggerSoundRef.current}
                  hotspot={hotspot}
                />
              ) : (
                <Hotspot
                  onClick={() => {
                    if (hotspot.config?.sound !== undefined)
                      onTriggerSoundHotspot(
                        hotspot,
                        isAllowTriggerSoundRef.current,
                      );
                    else if (
                      hotspot.type === 'point' &&
                      hotspot.config?.point?.isTransition &&
                      hotspot.config?.point?.typeTransition === '3d'
                    ) {
                      krpano.call(`skin_hotspotstyle_click(${name})`);
                      setTimeout(() => {
                        // if (hotspot.config?.sound !== undefined)
                        //   onTriggerSoundHotspot(
                        //     hotspot,
                        //     isAllowTriggerSoundRef.current,
                        //   );
                        // else
                        onClickHotspot({ hotspot });
                      }, 100);
                    } else onClickHotspot({ hotspot });
                  }}
                  isHidden={
                    hotspot.type === 'sound' && !hotspot.config?.sound?.action
                  }
                  isHideTitleGlobal={!!hotspotConfigGlobal?.hideTitle}
                  isHidePreviewGlobal={!!hotspotConfigGlobal?.hidePreview}
                  isHideTitleOnTopGlobal={
                    !!hotspotConfigGlobal?.hideTitleOnTopHotspot
                  }
                  bgColorDefault={hotspotConfigGlobal?.bgColor}
                  iconColorDefault={hotspotConfigGlobal?.iconColor}
                  hotspot={hotspot}
                  listScene={listScene}
                  listPost={listPost}
                  onChangePopover={visible => onChangeAutoChangeScene(!visible)}
                />
              ),
              node,
            );
          }
        }

        if (hotspot.polygon && hotspot.polygon_config) {
          onConfigHotspotPolygon(polygonHotspotConfigGlobal);
          krpano.set(`hotspot['${name}'].onclick`, () => {
            onClickHotspot({ hotspot });
          });
        } else if (!hotspot.polygon) {
          krpano.set(`hotspot['${name}'].ath`, +hotspot.ath);
          krpano.set(`hotspot['${name}'].atv`, +hotspot.atv);
          if (hotspot.type === 'chevron') {
            krpano.set(`hotspot['${name}'].style`, 'skin_hotspotstyle');
            krpano.set(`hotspot['${name}'].onclick`, () => {
              if (
                hotspot.config?.chevron?.isTransition &&
                hotspot.config?.chevron?.typeTransition === '3d'
              ) {
                krpano.call(`skin_hotspotstyle_click(${name})`);
                setTimeout(() => {
                  onClickHotspot({ hotspot });
                }, 100);
              } else onClickHotspot({ hotspot });
            });
          }
          if (hotspot.type === 'media') onConfigHotspotMedia();
          if (
            hotspot.type === 'instructor' &&
            hotspot.config?.instructor?.media?.src
          ) {
            onConfigHotspotInstructor(isAllowTriggerSoundRef.current);
            krpano.set(`hotspot['${name}'].onclick`, () => {
              onTriggerSoundHotspot(hotspot, isAllowTriggerSoundRef.current);
            });
          } else {
            krpano.set(`hotspot['${name}'].url`, Image.src);
            krpano.set(`hotspot['${name}'].renderer`, 'css3d');
          }

          krpano.set(`hotspot['${name}'].onloaded`, () => {
            if (hotspot.type !== 'chevron' && hotspot.type !== 'instructor')
              renderHotspot();
          });
        }

        if (!isReloadHotspot) krpano.call(`addhotspot('${name}')`);
        else renderHotspot();
      },
      [
        listScene,
        onClickHotspot,
        hotspotConfigGlobal,
        onTriggerSoundHotspot,
        onChangeAutoChangeScene,
        onAddHotspotKrpanoUntils,
        polygonHotspotConfigGlobal,
      ],
    );

    /**
     * SECTION: COMMENT_SCENE (COLLABORATION MODE)
     */

    const renderHTMLCommentSceneIcon = useCallback(
      async (krpano, name, commentScene: I_CommentScene) => {
        const hs = krpano.get(`hotspot[${name}]`);
        if (commentScene.isOwnerTour)
          krpano.set(`hotspot['${name}'].onDragend`, () => {
            const newData = {
              ath: krpano.get(`hotspot['${name}'].ath`),
              atv: krpano.get(`hotspot['${name}'].atv`),
            };
            if (
              Math.abs(Math.abs(commentScene.ath) - Math.abs(newData.ath)) <
                0.0001 &&
              Math.abs(Math.abs(commentScene.atv) - Math.abs(newData.atv)) <
                0.0001
            ) {
              console.log('not trigger event');
            } else
              onUpdateCommentScene({
                ...commentScene,
                ...newData,
                message: undefined,
                isResolved: undefined,
                scene_id: commentScene.scene,
              });
          });
        krpano.set(`hotspot['${name}'].onclick`, () => {
          setCommentSelected(commentScene);
        });
        let node = document.getElementById(name);
        if (node) {
          // NOTE: close all popover active on screen
          // NOTE: unmount old component when replace (fixed bug: 2 popover show same time)
          if (
            listCommentSceneIconRef.current.size &&
            listCommentSceneIconRef.current.get(commentScene.id)
          ) {
            listCommentSceneIconRef.current.get(commentScene.id).onClose();
          }
          await ReactDOM.unmountComponentAtNode(node);
        }
        const contentString = <div id={name} className="inline-flex" />;
        hs.sprite.innerHTML = renderToString(contentString).toString();
        node = document.getElementById(name);
        if (node) {
          ReactDOM.render(
            <CommentSceneIcon
              ref={r => {
                if (r) listCommentSceneIconRef.current.set(commentScene.id, r);
              }}
              commentScene={commentScene}
              onCreateCommentScene={onCreateCommentScene}
              onUpdateCommentScene={onUpdateCommentScene}
              onDeleteCommentScene={onDeleteCommentScene}
            />,
            node,
          );
        }
      },
      [
        setCommentSelected,
        onCreateCommentScene,
        onDeleteCommentScene,
        onUpdateCommentScene,
      ],
    );

    const onAddCommentSceneKrpano = useCallback(
      (commentScene: I_CommentScene, isReloadHTML = false) => {
        const { krpano, name } = onAddCommentSceneIconUntils(commentScene);
        if (commentScene.isOwnerTour)
          krpano.set(`hotspot['${name}'].ondown`, 'draghotspot()');
        krpano.set(`hotspot['${name}'].onloaded`, () => {
          renderHTMLCommentSceneIcon(krpano, name, commentScene);
        });
        if (!isReloadHTML) krpano.call(`addhotspot('${name}')`);
        else renderHTMLCommentSceneIcon(krpano, name, commentScene);
      },
      [renderHTMLCommentSceneIcon, onAddCommentSceneIconUntils],
    );

    const onRemoveCommentSceneKrpano = useCallback(
      id =>
        _window[krpanoId].call(
          `removehotspot('commentscene-${id}-${krpanoId}')`,
        ),
      [krpanoId],
    );

    const onReplaceCommentSceneKrpano = useCallback(
      (commentScene: I_CommentScene) =>
        onAddCommentSceneKrpano(commentScene, true),
      [onAddCommentSceneKrpano],
    );

    /**
     * !SECTION: END_COMMENT_SCENE (COLLABORATION MODE)
     */

    /**
     * SECTION: LISTEN EVENT
     * @event {load complete}
     * @event {on change view}
     * @event {on change zoom}
     */
    const onChangeView = debounce(() => onTriggerChangeView(), 150);

    const onChangeZoom = debounce(() => onTriggerChangeZoom(), 150);

    const onLoadComplete = useCallback(() => {
      if (scene && !isLoadedRef.current) {
        onApplyConfigPluginKrpanoForScene();

        if (scene?.config?.isLittlePlanet && isFirstScene)
          onApplyConfigLittlePlanetKrpano();

        onTriggerLoadCompleted(isTurnOffFollowMode);

        onAddHotspotNadirKrpano(nadirConfig);

        if (scene?.hotspots)
          scene?.hotspots?.forEach(item => onAddHotspotKrpano(item));

        // NOTE: init garrows plugin
        _window[krpanoId].call('garrows_init()');
        isLoadedRef.current = true;
        setIsLoaded(true);
      }
    }, [
      scene,
      krpanoId,
      nadirConfig,
      setIsLoaded,
      isFirstScene,
      onAddHotspotKrpano,
      isTurnOffFollowMode,
      onTriggerLoadCompleted,
      onAddHotspotNadirKrpano,
      onApplyConfigPluginKrpanoForScene,
      onApplyConfigLittlePlanetKrpano,
    ]);

    const onTriggerCreateCommentSceneKrpano = useCallback(() => {
      const x = _window[krpanoId].get('mouse.clickx');
      const y = _window[krpanoId].get('mouse.clicky');
      const location = _window[krpanoId].screentosphere(x, y);
      const commentSceneTemporary = {
        id: generateRandomID(),
        ath: location.x,
        atv: location.y,
        message: '',
        scene: scene.id,
        scene_id: scene.id,
      };
      setCommentSceneTemporary(commentSceneTemporary);
      setIsShowDialogCreateCommentScene(true);
    }, [
      scene,
      krpanoId,
      setCommentSceneTemporary,
      setIsShowDialogCreateCommentScene,
    ]);

    /**
     * !SECTION: END_LISTEN_EVENT
     */

    /**
     * SECTION: CROSS_FUNCTION
     */
    useImperativeHandle(
      ref,
      () => ({
        getContainer() {
          return containerRef.current;
        },
        // ! VIEW KRPANO
        viewPlanet() {
          _window[krpanoId].call('cm_littleplanet_view');
        },
        viewNormal() {
          _window[krpanoId].call('cm_normal_view');
        },
        viewMirrorBall() {
          _window[krpanoId].call('mirror_ball_view');
        },
        viewFullscreen() {
          document.body.requestFullscreen();
          // _window[krpanoId].set('fullscreen', true);
          _window[krpanoId].call(
            'tween(layer[fullscreen_exitbutton].alpha, 1)',
          );
        },
        viewWebvr() {
          _window[krpanoId].call('webvr.enterVR()');
        },
        toggleGyro() {
          _window[krpanoId].call('toggleGyroscope()');
        },
        startAutoRotate({ speed = 10, wait_time = 20 }) {
          _window[krpanoId].call(`startrotation(${speed}, ${wait_time})`);
        },
        stopAutoRotate() {
          _window[krpanoId].call('stoprotation');
        },
        toLeft() {
          _window[krpanoId].call('set(hlookat_moveforce,-0.75)');
        },
        toRight() {
          _window[krpanoId].call('set(hlookat_moveforce,+0.75)');
        },
        stopToHorizontal() {
          _window[krpanoId].call('set(hlookat_moveforce,0)');
        },
        toUp() {
          _window[krpanoId].call('set(vlookat_moveforce,-0.75)');
        },
        toDown() {
          _window[krpanoId].call('set(vlookat_moveforce,+0.75)');
        },
        stopToVertical() {
          _window[krpanoId].call('set(vlookat_moveforce,0)');
        },
        removeAllChevronHotspot(hotspots: I_Hotspot[]) {
          hotspots
            .filter(item => item.type === 'chevron')
            .forEach(item => {
              const name = `hotspot-${item.id}-${krpanoId}`;
              _window[krpanoId].call(`removenavigationelements(${name})`);
            });
        },
        triggerSound() {
          setIsAllowTriggerSound(true);
        },
        destroySound() {
          if (_window[krpanoId]) _window[krpanoId].call(`destroy_sound()`);
        },
        muteAllSound(detailScene: I_Scene) {
          detailScene?.hotspots?.forEach(hotspot => {
            const name = `hotspot-${hotspot.id}-${krpanoId}`;
            if (hotspot.type === 'sound' && hotspot.config?.sound)
              _window[krpanoId].call(`toggle_mute_sound(${`${name}`}, true)`);
            else if (
              hotspot.type === 'instructor' &&
              hotspot.config?.instructor?.media
            )
              _window[krpanoId].set(`hotspot[${name}].muted`, true);
            else if (
              hotspot.type === 'media' &&
              hotspot.config?.media?.type === 'media-video' &&
              hotspot?.config?.media?.media?.src
            )
              listHotspotMediaVideoRef.current
                .get(hotspot.id)
                .onToggleMute(true);
          });
        },
        unmuteAllSound(detailScene: I_Scene) {
          detailScene?.hotspots?.forEach(hotspot => {
            const name = `hotspot-${hotspot.id}-${krpanoId}`;
            if (hotspot.type === 'sound' && hotspot.config?.sound)
              _window[krpanoId].call(`toggle_mute_sound(${`${name}`}, false)`);
            else if (
              hotspot.type === 'instructor' &&
              hotspot.config?.instructor?.media
            )
              _window[krpanoId].set(`hotspot[${name}].muted`, false);
            else if (
              hotspot.type === 'media' &&
              hotspot.config?.media?.type === 'media-video' &&
              hotspot?.config?.media?.media?.src
            )
              listHotspotMediaVideoRef.current
                .get(hotspot.id)
                .onToggleMute(false);
          });
        },
        get(key: string) {
          _window[krpanoId].get(key);
        },
        set(key: string, value: string) {
          _window[krpanoId].set(key, value);
        },
        call(xml: string) {
          _window[krpanoId].call(xml);
        },
        onAddCommentScene(commentScene: I_CommentScene) {
          onAddCommentSceneKrpano(commentScene);
        },
        onRemoveCommentScene(id: string) {
          onRemoveCommentSceneKrpano(id);
        },
        onReplaceCommentScene(commentScene: I_CommentScene) {
          onReplaceCommentSceneKrpano(commentScene);
        },
        focusOnCommentScene(commentScene, func) {
          const call = `lookat(${commentScene.ath}, ${
            commentScene.atv
          }, null, null, true, true, ${func()})`;
          _window[krpanoId].call(call);
        },
      }),
      [
        krpanoId,
        onAddCommentSceneKrpano,
        onRemoveCommentSceneKrpano,
        onReplaceCommentSceneKrpano,
      ],
    );

    /**
     * SECTION: INIT_KRPANO
     */
    useEffect(() => {
      if (!krpanoView && krpanoId) setKrpanoView(KRPANO(krpanoId));
    }, [krpanoView, krpanoId]);

    useEffect(() => {
      if (krpanoView)
        krpanoView.init(
          divId,
          `${process.env.NEXT_PUBLIC_ASSETS_URL}/core/tour.xml`,
          {
            mypath: process.env.NEXT_PUBLIC_ASSETS_URL,
          },
        );
    }, [krpanoView, divId]);

    useEffect(() => {
      if (
        krpanoView &&
        urlTour &&
        tourEventStats.isVisiblePopupIntro !== undefined
      ) {
        setIsLoaded(false);
        isLoadedRef.current = false;
        if (scene && !tourEventStats.isVisiblePopupIntro)
          loadSceneToView(scene);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scene?.id, krpanoView, tourEventStats.isVisiblePopupIntro]);

    useEffect(() => {
      if (_window[krpanoId] && krpanoView) {
        _window[krpanoId].addEventListener('onloadcomplete', onLoadComplete);
        _window[krpanoId].addEventListener('onviewchange', onChangeView);
        _window[krpanoId].addEventListener('onmousewheel', onChangeZoom);
        _window[krpanoId].addEventListener(
          'ondoubleclick',
          onTriggerCreateCommentSceneKrpano,
        );
      }
      return () => {
        if (_window[krpanoId]) {
          _window[krpanoId].removeEventListener(
            'onloadcomplete',
            onLoadComplete,
          );
          _window[krpanoId].removeEventListener('onviewchange', onChangeView);
          _window[krpanoId].removeEventListener('onmousewheel', onChangeZoom);
          _window[krpanoId].removeEventListener(
            'ondoubleclick',
            onTriggerCreateCommentSceneKrpano,
          );
          setIsAllowTriggerSound(false);
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [krpanoView, scene]);

    /**
     * NOTE: trigger play_sound_at_hotspot (only when hotspot loaded)
     */
    useEffect(() => {
      if (isLoaded && isAllowTriggerSound)
        scene?.hotspots?.forEach(hotspot => {
          if (
            (hotspot.type === 'sound' &&
              hotspot.config?.sound !== undefined &&
              hotspot.config?.sound?.auto_play) ||
            (hotspot.type === 'instructor' &&
              hotspot.config?.instructor?.media &&
              !hotspot.config?.instructor?.paused_on_start)
          ) {
            onTriggerSoundHotspot(hotspot, isAllowTriggerSoundRef.current);
          } else if (
            hotspot.type === 'media' &&
            hotspot.config?.media?.type === 'media-video' &&
            hotspot?.config?.media?.volume &&
            listHotspotMediaVideoRef.current.get(hotspot.id)
          )
            listHotspotMediaVideoRef.current
              .get(hotspot.id)
              .onToggleMute(false);
        });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoaded, isAllowTriggerSound]);

    return (
      <div
        id={divId}
        ref={containerRef}
        style={{
          ...{
            width: '100%',
            height: '250px',
          },
          ...styleContainer,
        }}
      >
        {children}
      </div>
    );
  },
);

export default memo(KrpanoViewer);
